import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Select, Switch, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { geoExposureApi } from './geoExposureApi';
import { GeoExposureLayout } from './GeoExposureLayout';

const { Text } = Typography;

const toForm = (s) => ({ ...s, nextRunAt: s.nextRunAt ? dayjs(s.nextRunAt) : null });

export const GeoExposureSettings = () => {
    const [form] = Form.useForm();
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        geoExposureApi.getSettings().then((s) => { setSettings(s); form.setFieldsValue(toForm(s)); }).catch((e) => setError(e.message));
    }, [form]);

    const save = async (values) => {
        setSaving(true);
        try {
            const saved = await geoExposureApi.updateSettings({
                ...values,
                nextRunAt: values.nextRunAt ? values.nextRunAt.toISOString() : null,
            });
            setSettings(saved);
            form.setFieldsValue(toForm(saved));
            message.success('Configuration saved');
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <GeoExposureLayout title="Configuration">
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            <Card loading={!settings && !error} style={{ maxWidth: 720 }}>
                <Form form={form} layout="vertical" onFinish={save}>
                    <Form.Item name="rephraseCount" label="Rephrases per query" rules={[{ required: true }]} extra="Applies to queries added or regenerated from now on.">
                        <InputNumber min={1} max={20} />
                    </Form.Item>
                    <Form.Item name="runIntervalDays" label="Run interval (days)" rules={[{ required: true }]} extra="The next run is scheduled this long after a run finishes.">
                        <InputNumber min={1} max={90} />
                    </Form.Item>
                    <Form.Item name="schedulerEnabled" label="Scheduled runs" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="nextRunAt" label="Next run" extra="Set a time to start the schedule, or move the next run.">
                        <DatePicker showTime />
                    </Form.Item>
                    <Form.Item name="webSearch" label="Let ChatGPT search the web" valuePropName="checked" extra="Citations only appear when it does.">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="country" label="Country of the ChatGPT session" rules={[{ required: true, len: 2 }]}>
                        <Input style={{ width: 80 }} maxLength={2} />
                    </Form.Item>
                    <Form.Item name="brandTerms" label="Brand terms" extra="An answer containing one of these counts as a mention without asking the model.">
                        <Select mode="tags" tokenSeparators={[',']} />
                    </Form.Item>
                    <Form.Item name="minAnswerChars" label="Minimum answer length (characters)" extra="A shorter answer is treated as incomplete and asked again.">
                        <InputNumber min={0} max={5000} />
                    </Form.Item>
                    <Form.Item name="maxAttempts" label="Attempts per prompt">
                        <InputNumber min={1} max={10} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
                    {settings && <Text type="secondary" style={{ marginLeft: 12 }}>Last saved {new Date(settings.updatedAt).toLocaleString()}</Text>}
                </Form>
            </Card>
        </GeoExposureLayout>
    );
};
